import * as angular from "angular";

/**
 * AV-17974: thin wrapper over the Laravel /v2/xero endpoints. Everything is
 * read-only against Xero; link/unlink only touch our xerocontact rows.
 * Targets travel as `account` / `organisation`: the request interceptor
 * appends `campaignid=<active account>` to every call, so that name is taken.
 */
export class XeroService {
    private root = "/v2/xero";
    private $http: any;

    constructor($http: any) {
        this.$http = $http;
    }

    status(): Promise<object> {
        return this.$http.get(this.root + "/status");
    }

    connectUrl(): Promise<object> {
        return this.$http.get(this.root + "/connect");
    }

    disconnect(): Promise<object> {
        return this.$http.post(this.root + "/disconnect", {});
    }

    lookup(email: string): Promise<object> {
        return this.$http.get(this.root + "/lookup", {
            params: { email: email },
        });
    }

    contact(key: { account?: any; organisation?: any }): Promise<object> {
        return this.$http.get(this.root + "/contact", { params: key });
    }

    link(data: {
        account?: any;
        organisation?: any;
        xero_contact_id: string;
        matched_email: string;
        match_source: "email" | "manual";
    }): Promise<object> {
        return this.$http.post(this.root + "/link", data);
    }

    invoices(key: { account?: any; organisation?: any }): Promise<object> {
        return this.$http.get(this.root + "/invoices", { params: key });
    }

    /** Invoice deep link. */
    static invoiceUrl(id: string): string {
        return XeroService.app("invoicing/view/" + encodeURIComponent(id));
    }

    unlink(key: { account?: any; organisation?: any }): Promise<object> {
        return this.$http.post(this.root + "/unlink", key);
    }

    /** Org short code from /xero/status; deep links need it to target the right org. */
    static shortCode: string | null = null;

    private static app(path: string): string {
        // Xero returns the short code with its leading "!" (e.g. "!3-!Yd").
        const raw = XeroService.shortCode || "";
        const code = raw ? (raw.charAt(0) == "!" ? raw : "!" + raw) + "/" : "";
        return "https://go.xero.com/app/" + code + path;
    }

    /** The one place the Xero contact deep link is built. */
    static contactUrl(id: string): string {
        return XeroService.app("contacts/contact/" + encodeURIComponent(id));
    }

    contactUrl(id: string): string {
        return XeroService.contactUrl(id);
    }

    /**
     * Standing from the cached contact details, in Stripe's vocabulary so the
     * Payment status column reads the same for both platforms: archived in
     * Xero = cancelled, overdue balance = past due, otherwise active.
     */
    static standing(row: {
        xero_contact_status?: string | null;
        xero_outstanding?: string | null;
        xero_overdue?: string | null;
    }): { label: string; cls: string } {
        if (row.xero_contact_status == "ARCHIVED") {
            return { label: "cancelled", cls: "ec-pill-error" };
        }
        if (Number(row.xero_overdue) > 0) {
            return { label: "past due", cls: "ec-pill-error" };
        }
        return { label: "active", cls: "ec-pill-success" };
    }

    /**
     * The list row's xero_* columns from a contact link row (null = no link),
     * so a row updated in a dialog shows the same badge and standing as one
     * fresh from the server.
     */
    static listFields(row: any): { [column: string]: any } {
        return {
            xero_status: row ? row.status : null,
            xero_contact_id: row ? row.xero_contact_id : null,
            xero_name: row ? row.name : null,
            xero_outstanding: row ? row.outstanding : null,
            xero_overdue: row ? row.overdue : null,
            xero_currency: row ? row.currency : null,
            xero_contact_status: row ? row.contact_status : null,
        };
    }

    static label(status: string | null | undefined): string {
        switch (status) {
            case "matched":
                return "Xero";
            case "not_found":
                return "Not in Xero";
            case "ambiguous":
                return "Ambiguous in Xero";
            case "unmatched":
                return "Not linked";
            default:
                return "";
        }
    }
}

angular.module("vcServices").service("XeroService", XeroService);
